/// <reference types="react-scripts" />
import { Alert, Box, List, Typography } from "@mui/material";
import * as React from "react";
import { ToolButton } from "ui/Scripts/source/components/framework/toolButton";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { IFrontToDoItemGroup } from "../../../models/front/frontToDoItemGroup";
import * as frontService from "../../../models/services/frontService";
import * as tokenService from "../../../models/services/tokenService";
import { CardContainer } from "../../framework/CardContainer";
import { DashboardCardProgress } from "../../framework/DashboardCard/DashboardCardProgress";
import { DashboardCardTitle } from "../../framework/DashboardCard/DashboardCardTitle";
import { IFrontToDoItem } from "source/models/front/frontToDoItem";
import { DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { setWhistleBlowListDefaultFilter } from "source/models/store/whistleBlow";
import { WhistleBlowPage } from "source/components/whistleBlow/WhistleBlowPage";
import { customHistory } from "../../../framework/customHistory";
import { RecordPageList } from "source/components/record/RecordPage/RecordPageList/RecordPageList";
import { DashboardToDoItem } from "source/components/framework/DashboardStatistics";
import { useEffect, useRef, useState } from "react";
import { setRecordListDefaultFilter, setRecordReportDefaultFilter } from "source/models/store/record";
import { setEventListDefaultFilter } from "source/models/store/event";
import { EventPage } from "source/components/event/EventPage";
import { ISessionState } from "source/models/session/sessionState";

export interface IFrontToDoItemsProp {
    sessionState: ISessionState;
    disableGlobalLoading?: boolean;
    onPropertiesClick?: () => void;
}

interface IFrontToDoItemsState {
  isLoading: boolean;
  groups: IFrontToDoItemGroup[];
}

export const FrontToDoItems = (props: IFrontToDoItemsProp) => {
    const _isMounted = useRef(false);

    const [state, setState] = useState<IFrontToDoItemsState>({
        isLoading: false,
        groups: [],
    });

    const searchItems = async () => {
        setState((old) => ({ ...old, isLoading: true }));
        const result = await AppUtils.callService(
            () => frontService.getFrontToDoItemGroups(),
            false,
            props.disableGlobalLoading,
            () => {
                if (!_isMounted.current) return;
                setState((old) => ({ ...old, isLoading: false }));
            }
        );
        if (!result || !_isMounted.current) return;
        setState((old) => ({ ...old, groups: result.groups }));
    };

    useEffect(() => {
        _isMounted.current = true;

        return () => {
            _isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        searchItems();
    }, [props.sessionState?.culture ?? ""]);

    const handleGroupClick = (e: any, group: IFrontToDoItemGroup) => {
        e.stopPropagation();
        if (!group) return;
        const url = group.getRecordTypeIntraUrl();
        if (!url) return;
        tokenService.navigateTo(url);
    };

    const handleItemClick = (e: any, group: IFrontToDoItemGroup, item: IFrontToDoItem) => {
        e.stopPropagation();
        if (!item || !group) return;
        const url = group.getRecordTypeIntraUrl();
        if (!url) return;
        if (group.recordType === DatabaseRecordType.WhistleBlow) {
            setWhistleBlowListDefaultFilter(WhistleBlowPage.getDefaultListFilter(item));
            customHistory.push("/new/whistleblow/list");
        } else if (group.recordType === DatabaseRecordType.RecordUser) {
            const defaultReportFilter = RecordPageList.getDefaultReportFilter(item);
            if (defaultReportFilter) {
                setRecordReportDefaultFilter(defaultReportFilter);
                customHistory.push("/new/record/reports");
                return;
            }
            setRecordListDefaultFilter(RecordPageList.getDefaultListFilter(item));
            customHistory.push("/new/record/list");
        } else if (group.recordType === DatabaseRecordType.Event) {
            setEventListDefaultFilter(EventPage.getDefaultListFilter(item, props.sessionState?.code ?? ""));
            customHistory.push("/new/event/list");
        } else if (group.recordType === DatabaseRecordType.Task) {
            handleFrontTaskPanelToDoItemClick(item, props.sessionState);
        } else {
            tokenService.navigateTo(url);
        }
    };

    return (
        <>
            <DashboardCardTitle title={Translations.ToDo} />
            {!state.isLoading && state.groups.length < 1 && (
                <Alert severity="info">{Translations.NoToDos}</Alert>
            )}
            <CardContainer>
                <DashboardCardProgress
                    isLoading={!!props.disableGlobalLoading && state.isLoading}
                />
                <List>
                    {state.groups.length > 0 && state.groups.map((group) => (
                        <Box sx={{ margin: "0 0 1rem 0" }} key={group.recordType}>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    marginLeft: ".5rem",
                                    cursor: "pointer",
                                }}
                                onClick={(e) => handleGroupClick(e, group)}
                            >
                                {group.getRecordTypeTitle()}
                            </Typography>
                            {group.items.map((item) => (
                                <DashboardToDoItem key={item.title} toDoItem={item} onClick={(e, item) => handleItemClick(e, group, item)} />
                            ))}
                        </Box>
                    ))}
                </List>
            </CardContainer>
            {!!props.onPropertiesClick && (
                <ToolButton
                    tooltip={Translations.Properties}
                    classes={"properties"}
                    onClick={props.onPropertiesClick}
                />
            )}
        </>
    );
};

function handleFrontTaskPanelToDoItemClick(item: IFrontToDoItem, sessionState: ISessionState) {
    throw new Error("Function not implemented.");
}
